import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/integrations/sqlserver/client';

interface Period {
  Period: string;
  ReportCount: number;
}

interface PaginationMeta {
  page: number;
  limit: number;
  offset: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface PeriodsResponse {
  data: Period[];
  pagination: PaginationMeta;
}

interface UsePeriodsOptions {
  limit?: number;
}

export const usePeriods = (options: UsePeriodsOptions = {}) => {
  const { limit = 4 } = options;
  
  const [currentPage, setCurrentPage] = useState(1); // ✅ Internal page state
  const [periods, setPeriods] = useState<Period[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPeriods = useCallback(async (pageToFetch: number) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get('/periods', { 
        page: pageToFetch, 
        limit 
      }) as PeriodsResponse;
      
      // Only update if periods actually changed to prevent unnecessary re-renders
      setPeriods(prevPeriods => {
        const newPeriodIds = response.data.map(p => p.Period).sort().join(',');
        const prevPeriodIds = prevPeriods.map(p => p.Period).sort().join(',');
        
        if (newPeriodIds !== prevPeriodIds) {
          return response.data;
        } else {
          return prevPeriods; // Keep same reference to prevent re-renders
        }
      });
      
      setPagination(response.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching periods:', err);
    } finally {
      setLoading(false);
    }
  }, [limit]); // ✅ Remove currentPage dependency to prevent recreation

  // ✅ Initial fetch on mount only
  useEffect(() => {
    fetchPeriods(1); // Always start with page 1
  }, [fetchPeriods]);

  const goToPage = useCallback(async (newPage: number) => {
    setCurrentPage(newPage);
    await fetchPeriods(newPage);
  }, [fetchPeriods]);

  const nextPage = useCallback(async () => {
    if (pagination?.hasNextPage) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      await fetchPeriods(newPage);
    }
  }, [pagination, currentPage, fetchPeriods]);

  const prevPage = useCallback(async () => {
    if (pagination?.hasPrevPage) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      await fetchPeriods(newPage);
    }
  }, [pagination, currentPage, fetchPeriods]);

  const refresh = useCallback(() => {
    fetchPeriods(currentPage);
  }, [fetchPeriods, currentPage]);

  return {
    periods,
    pagination,
    loading,
    error,
    refresh,
    goToPage,
    nextPage,
    prevPage,
  };
}; 